import { useEffect, useRef, useState } from "react";

/**
 * Like {@link useFirstLoadPending}, but also resolves to `false` after `timeoutMs` even if
 * `isLoading` never becomes `false` — so a hung or slow query can't block whatever this gates
 * indefinitely. The timeout only ever widens the gate open sooner than `isLoading` would on its
 * own; it never re-closes it.
 *
 * `onTimeout`, if given, fires once when the timeout (rather than `isLoading` resolving normally)
 * is what opened the gate — pass a memoized callback (e.g. `useCallback`) so it doesn't retrigger
 * the underlying timer effect.
 */
export function useBoundedFirstLoadPending(isLoading: boolean, timeoutMs: number, onTimeout?: () => void): boolean {
    const hasResolvedRef = useRef(false);
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        if (!isLoading || hasResolvedRef.current || timedOut) {
            return;
        }
        const timer = setTimeout(() => {
            setTimedOut(true);
            onTimeout?.();
        }, timeoutMs);
        return () => clearTimeout(timer);
    }, [isLoading, timeoutMs, timedOut, onTimeout]);

    if (!isLoading) {
        hasResolvedRef.current = true;
    }
    return isLoading && !hasResolvedRef.current && !timedOut;
}
